import { DataTable, MainButton, PageWrapper } from '@repo/ui';
import { GetAllEntitiesSummaryResponse } from 'domain/models/responses/entities';
import { makeGetAllEntitiesSummary } from 'main/factories/usecases/entities/get-all-entities-summary-factory';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'reactstrap';

import { makeColumnsApiKeys } from '../common/columns/columns-api-keys';
import { ModalCreateApiKey } from '../components/modal-create-api-key';
import { ModalEditApiKey } from '../components/modal-edit-api-key';
import { ApiKey } from '../models';

const getAllEntitiesSummary = makeGetAllEntitiesSummary();

export const ApiKeys = () => {
  const { apiKeysService } = useService();

  const [apiKeysList, setApiKeysList] = useState<ApiKey[]>([]);
  const [entitiesList, setEntitiesList] = useState<
    GetAllEntitiesSummaryResponse[]
  >([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);

  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchApiKeys = useCallback(async () => {
    setLoadingApiKeys(true);
    try {
      const data = await apiKeysService.listApiKeys();
      setApiKeysList(data as ApiKey[]);
    } finally {
      setLoadingApiKeys(false);
    }
  }, [apiKeysService]);

  const fetchEntities = useCallback(async () => {
    try {
      const data = await getAllEntitiesSummary.execute();
      setEntitiesList(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchApiKeys();
    fetchEntities();
  }, [fetchApiKeys, fetchEntities]);

  const handleEditApiKey = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setIsModalEditOpen(true);
  };

  const handleToggleApiKey = async (apiKey: ApiKey) => {
    setTogglingId(apiKey.id);
    try {
      await apiKeysService.toggleApiKey({
        id: apiKey.id,
        active: !apiKey.active,
      });
      notifySuccess(
        !apiKey.active
          ? 'Chave de API ativada com sucesso!'
          : 'Chave de API desativada com sucesso!',
      );
      await fetchApiKeys();
    } catch (error) {
      notifyError(
        error instanceof Error
          ? error.message
          : 'Erro ao alterar a chave de API.',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const filteredApiKeysList = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return apiKeysList;

    return apiKeysList.filter((apiKey) =>
      apiKey.appName?.toLowerCase().includes(normalizedSearch),
    );
  }, [apiKeysList, search]);

  return (
    <PageWrapper title="Chaves de API">
      <ModalCreateApiKey
        isOpen={isModalCreateOpen}
        entitiesList={entitiesList}
        apiKeysService={apiKeysService}
        toggleModal={() => setIsModalCreateOpen(false)}
        onCreated={fetchApiKeys}
      />

      <ModalEditApiKey
        isOpen={isModalEditOpen}
        apiKey={selectedApiKey}
        apiKeysService={apiKeysService}
        toggleModal={() => setIsModalEditOpen(false)}
        onUpdated={fetchApiKeys}
      />

      <div className="mb-4">
        <h4 className="fw-bold">Chaves de API</h4>
        <p className="text-muted mb-0">
          Chaves de acesso de parceiros, escopadas por instituição.
        </p>
      </div>

      <Card className="p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div className="search-box" style={{ maxWidth: 320, width: '100%' }}>
            <input
              type="text"
              id="search"
              className="form-control search"
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <i className="ri-search-line search-icon"></i>
          </div>

          <MainButton onClick={() => setIsModalCreateOpen(true)}>
            <i className="ri-add-line" style={{ fontSize: '1rem' }} />
            Nova chave
          </MainButton>
        </div>
      </Card>

      <Card className="p-3">
        <DataTable
          noDataTitle="Nenhuma chave de API cadastrada"
          noDataDescription="Crie uma chave para liberar o acesso de um parceiro."
          data={filteredApiKeysList}
          loading={loadingApiKeys}
          columns={makeColumnsApiKeys(handleToggleApiKey, togglingId)}
          canSearch={false}
          defaultSortAsc={true}
          defaultSortFieldId={1}
          actions={[
            {
              id: 1,
              name: 'editar-chave-api',
              label: 'Editar',
              nameFeatherIcon: 'edit',
              classNameColorIcon: 'primary',
              onPress: (row: ApiKey) => handleEditApiKey(row),
            },
          ]}
        />
      </Card>
    </PageWrapper>
  );
};
